import React from "react";
import {
  IconButton,
  Box,
  Typography,
  Grid,
  TextField,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import { SlotsFormValues, TierType } from "../types/types";
import { toast } from "react-toastify";

interface TierProps {
  slotIndex: number;
  tiers: TierType[];
  onChange: (slotIndex: number, updatedTiers: TierType[]) => void;
  editable?: boolean;
}

interface TierInputFieldProps {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  variant?: "outlined" | "filled" | "standard";
  minWidth?: number | string;
  fullWidth?: boolean;
  inputProps?: any;
  error?: boolean;
  helperText?: string;
}

const TierComponent: React.FC<TierProps> = ({
  slotIndex,
  tiers,
  onChange,
  editable = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Define available MOQ options
  const moqOptions = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
  ];

  const handleAddTier = () => {
    const lastId = tiers.length > 0 ? tiers[tiers.length - 1].id : 0;

    const newTier: TierType = {
      id: lastId + 1,
      name: "",
      price: "",
      seatAllocation: "",
      status: "ACTIVE",
      moq: "1", // Default to minimum allowed value
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isEdited: false,
      isNew: true,
      newVersion: null,
    };

    onChange(slotIndex, [...tiers, newTier]);
  };

  const handleEditTier = (tierIndex: number, field: string, value: string) => {
    // Validate MOQ field
    if (field === "moq") {
      const numValue = parseInt(value);
      const isValidMoq = moqOptions.some(option => option.value === value);
      if (value !== "" && (!isValidMoq || isNaN(numValue))) {
        toast.error("Please select a valid MOQ");
        return;
      }
    }

    const updatedTiers = tiers.map((tier, index) => {
      if (index === tierIndex) {
        if (!tier.isNew) {
          return {
            ...tier,
            isEdited: true,
            newVersion: {
              ...tier.newVersion, // Keep previous changes if any
              [field]: value, // Apply the latest change
              updatedAt: new Date().toISOString(),
              createdAt: tier.createdAt,
            },
          };
        } else {
          // If it's a new tier, update directly
          return { ...tier, [field]: value };
        }
      }
      return tier;
    });
    onChange(slotIndex, updatedTiers as TierType[]);
  };

  // Handle seat allocation type change
  const handleSeatTypeChange = (tierIndex: number, seatType: string) => {
    if (seatType === "independent") {
      handleEditTier(tierIndex, "seatAllocation", "-1");
    } else if (seatType === "seats") {
      // If switching to enter seat, reset to empty string so user can input
      const currentValue =
        tiers[tierIndex].newVersion?.seatAllocation ??
        tiers[tierIndex].seatAllocation;
      if (currentValue === "-1") {
        handleEditTier(tierIndex, "seatAllocation", "");
      }
    }
  };

  // Get current seat allocation type
  const getSeatAllocationType = (tier: TierType) => {
    const currentValue = tier.newVersion?.seatAllocation ?? tier.seatAllocation;
    return currentValue === "-1" ? "independent" : "seats";
  };

  const handleRemoveTier = (tierIndex: number) => {
    const updatedTiers = tiers
      .map((tier, index) => {
        if (index === tierIndex) {
          if (tier.isNew) {
            return null; // ✅ Remove new tiers completely
          }
          return {
            ...tier,
            status: "INACTIVE",
            isEdited: false,
            newVersion: null,
          }; // ✅ Mark existing tiers as inactive
        }
        return tier;
      })
      .filter(Boolean); // ✅ Remove `null` values from the array

    onChange(slotIndex, updatedTiers as TierType[]);
  };

  // MOQ Dropdown Field Component
  const MOQDropdownField: React.FC<{
    tier: TierType;
    tierIndex: number;
    disabled: boolean;
    variant: "outlined" | "filled" | "standard";
    fullWidth?: boolean;
  }> = ({ tier, tierIndex, disabled, variant, fullWidth = false }) => {
    const currentValue = tier.newVersion?.moq ?? tier.moq;

    return (
      <Box
        sx={{
          minWidth: fullWidth ? "100%" : 80,
          width: fullWidth ? "100%" : "auto",
        }}
      >
        <FormControl
          variant={variant}
          fullWidth={fullWidth}
          disabled={disabled}
        >
          <InputLabel sx={{ fontSize: "14px" }}>MOQ</InputLabel>
          <Select
            value={currentValue || "1"}
            onChange={(e) =>
              handleEditTier(tierIndex, "moq", e.target.value)
            }
            label="MOQ"
            sx={{
              "& .MuiSelect-select": {
                fontSize: "14px",
                padding: { xs: "4px 0", sm: "6px 0" },
              },
            }}
          >
            {moqOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  // Seat Allocation Field Component - Option 2: Same Line Dropdown + Input
  const SeatAllocationFieldSameLine: React.FC<{
    tier: TierType;
    tierIndex: number;
    disabled: boolean;
    variant: "outlined" | "filled" | "standard";
    fullWidth?: boolean;
  }> = ({ tier, tierIndex, disabled, variant, fullWidth = false }) => {
    const seatType = getSeatAllocationType(tier);
    const currentValue = tier.newVersion?.seatAllocation ?? tier.seatAllocation;

    return (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
          minWidth: fullWidth ? "100%" : 180,
          width: fullWidth ? "100%" : "auto",
        }}
      >
        <FormControl
          variant={variant}
          disabled={disabled}
          sx={{ minWidth: 100, flex: seatType === "independent" ? 1 : 0.6 }}
        >
          <InputLabel sx={{ fontSize: "14px" }}>Type</InputLabel>
          <Select
            value={seatType}
            onChange={(e) => handleSeatTypeChange(tierIndex, e.target.value)}
            label="Type"
            sx={{
              "& .MuiSelect-select": {
                fontSize: "14px",
                padding: { xs: "4px 0", sm: "6px 0" },
              },
            }}
          >
            <MenuItem value="independent">No Limit</MenuItem>
            <MenuItem value="seats">Set Maximum Seats</MenuItem>
          </Select>
        </FormControl>

        {seatType === "seats" && (
          <TextField
            label="Seats"
            type="number"
            value={currentValue === "-1" ? "" : currentValue}
            onChange={(e) =>
              handleEditTier(tierIndex, "seatAllocation", e.target.value)
            }
            disabled={disabled}
            variant={variant}
            sx={{
              flex: 0.4,
              minWidth: 70,
              "& .MuiInputBase-input": {
                fontSize: "14px",
                padding: { xs: "4px 0", sm: "6px 0" },
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
              },
            }}
          />
        )}
      </Box>
    );
  };

  // Choose which implementation to use (change this to switch between options)
  const SeatAllocationField = SeatAllocationFieldSameLine; // or SeatAllocationFieldCombined

  return (
    <Box
      sx={{
        mt: { xs: 5, sm: 4 },
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: { xs: "26px 12px 16px", sm: "20px" },
        borderRadius: { xs: "20px", sm: "30px" },
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
        transition: "all 0.2s ease",
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <Typography
        sx={{
          position: "absolute",
          top: { xs: "-12px", sm: "-18px" },
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#F5F7FA",
          padding: "2px 10px",
          borderRadius: "20px",
          fontWeight: 600,
          fontSize: { xs: "16px", sm: "18px" },
          textAlign: "center",
        }}
      >
        Tier
      </Typography>

      {tiers?.map((tier, index) => (
        <Box
          key={index}
          sx={{
            p: { xs: 1, sm: 2 },
            borderRadius: "8px",
            border: "1px solid #D5D7DA",
            overflowX: { xs: "auto", md: "visible" },
          }}
        >
          {isMobile ? (
            // Mobile layout - stacked fields
            <Box display="flex" flexDirection="column" gap={2}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={14} fontWeight={500}>
                  Tier {tier.id + 1}
                </Typography>
                {tiers.length > 1 && (
                  <IconButton
                    disabled={tier.status === "INACTIVE" || !editable}
                    onClick={() => handleRemoveTier(index)}
                    sx={{ color: "red", padding: "4px" }}
                    size="small"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TierInputField
                    label="Tier Name"
                    type="text"
                    name="name"
                    value={tier.newVersion?.name ?? tier.name}
                    onChange={(e) =>
                      handleEditTier(index, "name", e.target.value)
                    }
                    disabled={tier.status === "INACTIVE" || !editable}
                    variant="standard"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TierInputField
                    label="Price"
                    name="price"
                    type="number"
                    value={tier.newVersion?.price ?? tier.price}
                    onChange={(e) =>
                      handleEditTier(index, "price", e.target.value)
                    }
                    disabled={tier.status === "INACTIVE" || !editable}
                    variant="standard"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <SeatAllocationField
                    tier={tier}
                    tierIndex={index}
                    disabled={tier.status === "INACTIVE" || !editable}
                    variant="standard"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <MOQDropdownField
                    tier={tier}
                    tierIndex={index}
                    disabled={tier.status === "INACTIVE" || !editable}
                    variant="standard"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TierInputField
                    label="Status"
                    name="status"
                    value={tier.status}
                    onChange={() => {}}
                    disabled={true}
                    variant="standard"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TierInputField
                    label="Updated At"
                    name="updatedAt"
                    type="text"
                    value={new Date(tier.updatedAt).toLocaleString()}
                    onChange={() => {}}
                    disabled={true}
                    variant="standard"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TierInputField
                    label="Created At"
                    name="createdAt"
                    type="text"
                    value={new Date(tier.createdAt).toLocaleString()}
                    onChange={() => {}}
                    disabled={true}
                    variant="standard"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          ) : isTablet ? (
            // Tablet layout - semi-condensed
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={1}>
                <Typography fontSize={14} fontWeight={500} sx={{ mt: 2 }}>
                  {tier.id + 1}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <TierInputField
                  label="Tier Name"
                  type="text"
                  name="name"
                  value={tier.newVersion?.name ?? tier.name}
                  onChange={(e) =>
                    handleEditTier(index, "name", e.target.value)
                  }
                  disabled={tier.status === "INACTIVE" || !editable}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={2}>
                <TierInputField
                  label="Price"
                  name="price"
                  type="number"
                  value={tier.newVersion?.price ?? tier.price}
                  onChange={(e) =>
                    handleEditTier(index, "price", e.target.value)
                  }
                  disabled={tier.status === "INACTIVE" || !editable}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={2}>
                <SeatAllocationField
                  tier={tier}
                  tierIndex={index}
                  disabled={tier.status === "INACTIVE" || !editable}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={1}>
                <MOQDropdownField
                  tier={tier}
                  tierIndex={index}
                  disabled={tier.status === "INACTIVE" || !editable}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={2}>
                <TierInputField
                  label="Status"
                  name="status"
                  value={tier.status}
                  onChange={() => {}}
                  disabled={true}
                  variant="standard"
                  fullWidth
                />
              </Grid>
              <Grid item xs={1}>
                {tiers.length > 1 && (
                  <IconButton
                    disabled={tier.status === "INACTIVE" || !editable}
                    onClick={() => handleRemoveTier(index)}
                    sx={{ color: "red", mt: 1 }}
                    size="small"
                  >
                    <Close />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ) : (
            // Desktop layout - original horizontal
            <Box
              display="flex"
              gap={2}
              alignItems="flex-start"
              flexWrap="nowrap"
            >
              <Box fontSize={14} fontWeight={500} sx={{ mt: 2 }}>
                {tier.id + 1}
              </Box>
              <TierInputField
                label="Tier Name"
                type="text"
                name="name"
                value={tier.newVersion?.name ?? tier.name}
                onChange={(e) => handleEditTier(index, "name", e.target.value)}
                disabled={tier.status === "INACTIVE" || !editable}
                variant="standard"
              />
              <TierInputField
                label="Price"
                name="price"
                type="number"
                value={tier.newVersion?.price ?? tier.price}
                onChange={(e) => handleEditTier(index, "price", e.target.value)}
                disabled={tier.status === "INACTIVE" || !editable}
                variant="standard"
              />
              <SeatAllocationField
                tier={tier}
                tierIndex={index}
                disabled={tier.status === "INACTIVE" || !editable}
                variant="standard"
              />
              <MOQDropdownField
                tier={tier}
                tierIndex={index}
                disabled={tier.status === "INACTIVE" || !editable}
                variant="standard"
              />
              <TierInputField
                label="Status"
                name="status"
                value={tier.status}
                onChange={() => {}}
                disabled={true}
                variant="standard"
              />
              <TierInputField
                label="Updated At"
                name="updatedAt"
                type="text"
                value={new Date(tier.updatedAt).toLocaleString()}
                onChange={() => {}}
                disabled={true}
                variant="standard"
                minWidth={150}
              />
              <TierInputField
                label="Created At"
                name="createdAt"
                type="text"
                value={new Date(tier.createdAt).toLocaleString()}
                onChange={() => {}}
                disabled={true}
                variant="standard"
                minWidth={150}
              />
              {tiers.length > 1 && (
                <IconButton
                  disabled={tier.status === "INACTIVE" || !editable}
                  onClick={() => handleRemoveTier(index)}
                  sx={{ color: "red", mt: 1 }}
                >
                  <Close />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      ))}

      {/* Add Tier Button */}
      {/* {tiers.filter((tier) => tier.status === "ACTIVE").length < 10 && ( */}
        <IconButton
          onClick={handleAddTier}
          disabled={!editable}
          sx={{
            position: "absolute",
            bottom: { xs: "-12px", sm: "-10px" },
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#fff",
            border: "1px solid #D5D7DA",
            width: { xs: "28px", sm: "24px" },
            height: { xs: "28px", sm: "24px" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            "&:hover": { backgroundColor: "#f0f0f0" },
          }}
        >
          <Add fontSize="small" />
        </IconButton>
      {/* )} */}
    </Box>
  );
};

const TierInputField: React.FC<TierInputFieldProps> = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  disabled = false,
  variant = "outlined",
  minWidth = 50,
  fullWidth = false,
  inputProps,
  error = false,
  helperText = "",
}) => {
  return (
    <Box sx={{ minWidth, width: fullWidth ? "100%" : "auto" }}>
      <TextField
        name={name}
        label={label}
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        variant={variant}
        fullWidth={fullWidth}
        inputProps={inputProps}
        error={error}
        helperText={helperText}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: "14px",
            padding: { xs: "4px 0", sm: "6px 0" },
          },
          "& .MuiInputLabel-root": {
            fontSize: "14px",
          },
          "& .MuiFormHelperText-root": {
            fontSize: "12px",
          },
        }}
      />
    </Box>
  );
};

export default TierComponent;